/* Performance chart — §9.4.
 *
 * Two series: market value and contributed capital. The area between them is the
 * only part that is performance, so it is the part that gets filled.
 *
 * The line is drawn solid between measured points and dashed across gaps. It
 * never interpolates a smooth curve through a hole in the record, because that
 * would invent data the book does not have (§9.2).
 *
 * Not every stretch without a reading is a hole, though. Overnight and at the
 * weekend the exchange is shut, so the value stood exactly where it was last
 * measured — and saying "no data" about a night when there was no data to have
 * is as wrong in its own direction as inventing a curve. Those stretches are
 * drawn as what happened: the last figure carried flat, then a step to the next
 * reading. No value between the two is invented; the line states the one that
 * was standing. */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { amountsHidden, money, moneySigned, pctSigned, shortDate } from '../lib/format';
import { ticksFor } from '../lib/ticks';
import {
  atResolution, gapLimit, perDayResolution, rangeEnd, rangeStart, tradedBetween,
} from '../state/series';
import type { ChartMode, CustomRange, RangeKey } from '../state/series';
import type { Snapshot } from '../types';

/* The window and mode types are the chart's vocabulary but not its property —
   the header above it speaks the same one. They live in state/series. */
export type { ChartMode, CustomRange, RangeKey } from '../state/series';

export interface BenchmarkSeries {
  label: string;
  days: Array<{ date: string; close: number }>;
}

interface Props {
  snapshots: Snapshot[];
  /** Drawn against the book, each rebased to it. */
  benchmarks?: BenchmarkSeries[];
  mode: ChartMode;
  range: RangeKey;
  custom: CustomRange;
  currency: 'USD' | 'PLN';
  toDisplay: (usd: number) => number;
  onScrub: (point: ScrubPoint | null) => void;
  /** Draw the money-paid-in line and the area between it and the value. Off
      leaves the value line alone against its own scale, which is the shape of
      the book rather than the shape of the book above what went into it. */
  showPaidIn?: boolean;
}

export interface ScrubPoint {
  at: string;
  totalUsd: number;
  contributedUsd: number;
  gainUsd: number;
  returnPct: number;
}

const W = 340;
const H = 128;
const PAD_X = 4;
const PAD_Y = 10;

/* Told apart by dash pattern as well as by shade, because §11 says colour is
   never the only signal and two grey dashed lines are one grey dashed line. */
const COMPARISON = [
  { stroke: 'var(--dim)', dash: '5 3' },
  { stroke: 'var(--dim2)', dash: '2 3' },
  { stroke: 'var(--dim)', dash: '8 3 2 3' },
  { stroke: 'var(--dim2)', dash: '1 4' },
];

/* Above this many points on screen, the dots come off.
 *
 * §9.2 marks measured points so they can be told from the line drawn between
 * them, and that is worth doing for the three values somebody typed in — a line
 * through three points with nothing to say so reads as continuous data. It is
 * worth nothing at all for a book with a year of readings: the dots stop being
 * marks and become a thickening of the line, which is what they were doing
 * here. A dozen is about where one stops being the other. */
const DOT_LIMIT = 12;

export function PerformanceChart({
  snapshots, mode, range, custom, currency, toDisplay, onScrub, benchmarks,
  showPaidIn = true,
}: Props) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [cursor, setCursor] = useState<number | null>(null);

  const points = useMemo(() => {
    const from = rangeStart(range, custom);
    const to = rangeEnd(range, custom);
    /* §9.4: a day or a week keeps every reading; anything wider is drawn a day
       at a time. Keyed to the window rather than to the age of the point —
       keyed to age, today's readings still stacked into one column of pixels on
       a monthly chart and drew a vertical bar out of a few ordinary hours. */
    const perDay = perDayResolution(range, custom);
    return atResolution(snapshots, Date.now(), perDay)
      .map((s) => ({ ...s, t: new Date(s.at).getTime() }))
      .filter((s) => Number.isFinite(s.t) && s.t >= from && s.t <= to)
      .sort((a, b) => a.t - b.t);
  }, [snapshots, range, custom]);

  const series = useMemo(() => {
    return points.map((p) => {
      const gain = p.totalUsd - p.contributedUsd;
      const ret = p.contributedUsd > 0 ? (gain / p.contributedUsd) * 100 : 0;
      return {
        t: p.t,
        at: p.at,
        totalUsd: p.totalUsd,
        contributedUsd: p.contributedUsd,
        gainUsd: gain,
        returnPct: ret,
        // Which number this mode actually plots.
        reconstructed: p.reconstructed === true,
        primary: mode === 'value' ? p.totalUsd : mode === 'gains' ? gain : ret,
        /* A snapshot with no contributed figure means the cash flows for that
           date were never recorded — not that nothing was paid in. Plotting it
           as a real zero would draw a line that is not true and squash the value
           series into the top of the chart, so it is left out entirely. */
        secondary: mode === 'value' && p.contributedUsd > 0 ? p.contributedUsd : null,
      };
    });
  }, [points, mode]);

  /* The benchmark, rebased to where the book started in this window.
   *
   * Drawn as a value, not as a percentage on a second axis: anchoring both at
   * the same first point means the gap between the lines *is* the difference in
   * performance, readable by eye without arithmetic. A second axis would let two
   * unrelated scales be lined up to say whatever the scaling chose.
   *
   * Only in the value mode. Against "gains only" or "return %" it would be
   * comparing different quantities and inviting a conclusion neither supports. */
  const benchLines = useMemo(() => {
    if (!benchmarks?.length || mode !== 'value' || series.length === 0) return [];
    const first = series[0]!;
    const startDay = first.at.slice(0, 10);
    const endMs = series[series.length - 1]!.t;

    return benchmarks.map((b) => {
      const usable = b.days.filter((d) => d.date >= startDay);
      const base = usable[0]?.close;
      if (!base || base <= 0) return { label: b.label, points: [] };
      return {
        label: b.label,
        points: usable
          .map((d) => ({
            t: new Date(`${d.date}T21:00:00.000Z`).getTime(),
            v: first.primary * (d.close / base),
          }))
          .filter((p) => Number.isFinite(p.t) && p.t <= endMs),
      };
    }).filter((b) => b.points.length > 1);
  }, [benchmarks, mode, series]);

  const scale = useMemo(() => {
    if (series.length === 0) return null;
    const values = [
      ...series.flatMap((s) => (showPaidIn && s.secondary !== null ? [s.primary, s.secondary] : [s.primary])),
      // Every comparison has to fit, or the line it is drawn against is cropped.
      ...benchLines.flatMap((b) => b.points.map((p) => p.v)),
    ];
    let lo = Math.min(...values);
    let hi = Math.max(...values);
    if (mode !== 'return' && mode !== 'gains') lo = Math.min(lo, hi * 0.985);
    if (lo === hi) { lo -= 1; hi += 1; }
    const padding = (hi - lo) * 0.12;
    lo -= padding; hi += padding;
    /* x is spaced by position in the record, not by the clock.
     *
     * A week is a hundred and sixty-eight hours of which about thirty-two are
     * a session. Spaced by the clock, four fifths of the chart's width goes to
     * drawing a flat line through hours when nothing could have happened, and
     * what is left is five narrow bursts of actual movement separated by
     * plateaus and vertical steps — which is what made a week look like a row
     * of blocks rather than a line. Every intraday chart in finance compresses
     * to trading time for the same reason.
     *
     * Nothing is claimed that was not measured: the readings are the same
     * readings in the same order, a gap in the record is still dashed, and the
     * cursor still reports each point's own date and time. Only the spacing
     * between them changes.
     *
     * Anything not in the series — a comparison line, which has its own dates —
     * is placed by interpolating between the two readings it falls between, so
     * the same day still lands on the same x for both. */
    const n = series.length;
    const xs = series.map((_, i) => (n === 1 ? W / 2 : PAD_X + (i / (n - 1)) * (W - PAD_X * 2)));
    const exact = new Map(series.map((p, i) => [p.t, xs[i]!]));
    const t0 = series[0]!.t;
    const t1 = series[n - 1]!.t;

    const x = (t: number): number => {
      const hit = exact.get(t);
      if (hit !== undefined) return hit;
      if (t <= t0) return xs[0]!;
      if (t >= t1) return xs[n - 1]!;
      let hi_ = 1;
      while (hi_ < n && series[hi_]!.t < t) hi_++;
      const lo_ = hi_ - 1;
      const a = series[lo_]!;
      const b = series[hi_]!;
      const span = b.t - a.t;
      const f = span === 0 ? 0 : (t - a.t) / span;
      return xs[lo_]! + f * (xs[hi_]! - xs[lo_]!);
    };

    return {
      x,
      y: (v: number) => PAD_Y + (1 - (v - lo) / (hi - lo)) * (H - PAD_Y * 2),
      t0, t1, lo, hi,
      /* Round readings to measure the line against. Without them the line has a
         shape and no size: a book that moved two hundred dollars and one that
         moved twenty thousand draw the same picture. */
      ticks: ticksFor(lo, hi),
    };
  }, [series, mode, benchLines, showPaidIn]);

  /* Segments: consecutive runs of points close enough to be one line. A gap
     wider than the series' own step breaks the run, and the two ends get joined
     by a dashed bridge so the eye reads an absence rather than a trend. */
  const segments = useMemo(() => {
    if (!scale) return [];
    const limit = gapLimit(series.map((p) => p.t));
    const runs: Array<typeof series> = [];
    let current: typeof series = [];
    for (let i = 0; i < series.length; i++) {
      const p = series[i]!;
      const prev = series[i - 1];
      if (prev && p.t - prev.t > limit) { runs.push(current); current = []; }
      current.push(p);
    }
    if (current.length) runs.push(current);
    return runs.filter((r) => r.length > 0);
  }, [series, scale]);

  /* Chains: what the eye should read as one unbroken line.
   *
   * A gap in the readings is not automatically a gap in the record. Overnight
   * and at the weekend the exchange is shut and the book stood exactly where it
   * was last measured, so that stretch is drawn as what happened — flat, then a
   * step. Only a stretch covering a session nobody recorded is a hole.
   *
   * Both were already drawn correctly as *lines*. What was not was everything
   * else: the filled area and the money-paid-in line were built one segment at
   * a time, so at every night and every weekend they stopped dead and started
   * again a few pixels later. On a week's chart that is four or five hard
   * vertical cliffs down to nothing, which reads as the book having gone
   * missing five times — an artefact of how the shape was assembled, describing
   * nothing that happened.
   *
   * So the step belongs in the geometry rather than in a patch drawn over the
   * top of it. A chain is a run of segments joined by nights, flattened into
   * one list of vertices with the carry-flat-then-step written in. Fill, value
   * line and paid-in line are all built from that same list, so all three break
   * in exactly one place — a real hole — and nowhere else. */
  type Vertex = { x: number; y1: number; y2: number | null };

  const chains = useMemo(() => {
    if (!scale) return [];
    const out: Vertex[][] = [];
    let current: Vertex[] = [];

    const push = (p: typeof series[number]) => {
      current.push({
        x: scale.x(p.t),
        y1: scale.y(p.primary),
        y2: p.secondary !== null ? scale.y(p.secondary) : null,
      });
    };

    for (let i = 0; i < segments.length; i++) {
      const run = segments[i]!;
      if (i > 0) {
        const a = segments[i - 1]!.at(-1)!;
        const b = run[0]!;
        if (tradedBetween(a.t, b.t)) {
          // A session went unrecorded. The line genuinely stops here.
          out.push(current);
          current = [];
        } else {
          /* The market was shut. The figure stood where it was until the next
             reading, so the corner goes in as two vertices at the new x: the
             old level carried across, then the step up or down to it. */
          const bx = scale.x(b.t);
          current.push({
            x: bx,
            y1: scale.y(a.primary),
            y2: a.secondary !== null ? scale.y(a.secondary) : null,
          });
        }
      }
      for (const p of run) push(p);
    }
    if (current.length) out.push(current);
    return out.filter((c) => c.length > 1);
  }, [segments, scale, series]);

  /* The dashed bridge across a hole, and nothing else — the nights are inside
     the chains now. */
  const holes = useMemo(() => {
    if (!scale) return [];
    const out: Array<{ d: string; key: string }> = [];
    for (let i = 1; i < segments.length; i++) {
      const a = segments[i - 1]!.at(-1)!;
      const b = segments[i]![0]!;
      if (!tradedBetween(a.t, b.t)) continue;
      out.push({
        key: `${a.at}-${b.at}`,
        d: `M${scale.x(a.t).toFixed(2)},${scale.y(a.primary).toFixed(2)} `
          + `L${scale.x(b.t).toFixed(2)},${scale.y(b.primary).toFixed(2)}`,
      });
    }
    return out;
  }, [segments, scale]);

  const heldShut = useMemo(
    () => segments.some((run, i) => i > 0 && !tradedBetween(segments[i - 1]!.at(-1)!.t, run[0]!.t)),
    [segments],
  );

  const line = useCallback(
    (chain: Vertex[], pick: (v: Vertex) => number | null) => chain
      .map((v) => (pick(v) === null ? null : `${v.x.toFixed(2)},${pick(v)!.toFixed(2)}`))
      .filter((s): s is string => s !== null)
      .map((s, i) => `${i === 0 ? 'M' : 'L'}${s}`)
      .join(' '),
    [],
  );

  /* The filled area between value and contributed — the performance itself.
     Broken only where a vertex has no contributed figure: that date's cash
     flows were never recorded, and a floor drawn at zero would claim they were.
  */
  const areaPath = useMemo(() => {
    if (!scale || mode !== 'value') return '';
    const floor = H - PAD_Y / 2;
    const out: string[] = [];
    for (const chain of chains) {
      let run: Vertex[] = [];
      const flush = () => {
        if (run.length > 1) {
          const top = run.map((v) => `${v.x.toFixed(2)},${v.y1.toFixed(2)}`);
          const bottom = showPaidIn
            ? run.slice().reverse().map((v) => `${v.x.toFixed(2)},${v.y2!.toFixed(2)}`)
            : [`${run.at(-1)!.x.toFixed(2)},${floor}`, `${run[0]!.x.toFixed(2)},${floor}`];
          out.push(`M${top.join(' L')} L${bottom.join(' L')} Z`);
        }
        run = [];
      };
      for (const v of chain) {
        if (showPaidIn && v.y2 === null) flush();
        else run.push(v);
      }
      flush();
    }
    return out.join(' ');
  }, [chains, scale, mode, showPaidIn]);

  /* ---- scrubbing, on pointer and touch alike (§9.4) ---- */

  const nearest = useCallback((clientX: number) => {
    const svg = svgRef.current;
    if (!svg || !scale || series.length === 0) return null;
    const box = svg.getBoundingClientRect();
    const xInView = ((clientX - box.left) / box.width) * W;
    let best = series[0]!;
    let bestDist = Infinity;
    for (const p of series) {
      const d = Math.abs(scale.x(p.t) - xInView);
      if (d < bestDist) { bestDist = d; best = p; }
    }
    return best;
  }, [scale, series]);

  /* One reading per frame, and only once the finger reaches a different point.
     A touch reports movement far more often than the screen redraws, and every
     report used to set state here and in the card above it — redrawing the line
     and the headline for a finger that had not left the point it was on. */
  const pendingX = useRef<number | null>(null);
  const frame = useRef<number | null>(null);
  const shownT = useRef<number | null>(null);

  const apply = useCallback((clientX: number) => {
    const p = nearest(clientX);
    if (!p || p.t === shownT.current) return;
    shownT.current = p.t;
    setCursor(p.t);
    onScrub({
      at: p.at, totalUsd: p.totalUsd, contributedUsd: p.contributedUsd,
      gainUsd: p.gainUsd, returnPct: p.returnPct,
    });
  }, [nearest, onScrub]);

  const handleMove = useCallback((clientX: number) => {
    pendingX.current = clientX;
    if (frame.current !== null) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = null;
      if (pendingX.current !== null) apply(pendingX.current);
    });
  }, [apply]);

  const end = useCallback(() => {
    if (frame.current !== null) { cancelAnimationFrame(frame.current); frame.current = null; }
    pendingX.current = null;
    shownT.current = null;
    setCursor(null);
    onScrub(null);
  }, [onScrub]);

  useEffect(() => () => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
  }, []);

  if (!scale || series.length === 0) {
    return (
      <div className="empty">
        Nothing measured in this range yet. The book records one value per hour
        while the app is open, and you can type in past values in settings.
      </div>
    );
  }

  const cursorPoint = cursor === null ? null : series.find((p) => p.t === cursor) ?? null;
  const zeroY = mode !== 'value' && scale.lo < 0 && scale.hi > 0 ? scale.y(0) : null;

  const fmt = (v: number) =>
    mode === 'return' ? pctSigned(v) : mode === 'gains'
      ? moneySigned(toDisplay(v), currency) : money(toDisplay(v), currency);

  return (
    <div className="chart-wrap">
      <svg
        ref={svgRef}
        className="chart"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        style={{ height: H }}
        role="img"
        aria-label={
          `${mode === 'value' ? 'Value against money paid in' :
             mode === 'gains' ? 'Gains only' : 'Return percentage'}, ` +
          `${shortDate(series[0]!.at)} to ${shortDate(series.at(-1)!.at)}. ` +
          `Latest ${fmt(series.at(-1)!.primary)}.`
        }
        onPointerDown={(e) => {
          // Move first: capture is an optimisation, and if it throws — which it
          // does for a pointer the element never saw — scrubbing must not die
          // with it.
          handleMove(e.clientX);
          try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* keep scrubbing */ }
        }}
        onPointerMove={(e) => { if (e.buttons > 0 || e.pointerType === 'touch') handleMove(e.clientX); }}
        onPointerUp={end}
        onPointerCancel={end}
        onPointerLeave={end}
      >
        <defs>
          <linearGradient id="perf-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.30" />
            <stop offset="100%" stopColor="var(--accent)" stopOpacity="0.03" />
          </linearGradient>
        </defs>

        {/* Behind everything else on the chart. */}
        {scale.ticks.map((v) => (
          <line key={v} x1={0} y1={scale.y(v)} x2={W} y2={scale.y(v)}
                stroke="var(--edge)" strokeWidth="1" />
        ))}

        {zeroY !== null ? (
          <line x1={PAD_X} y1={zeroY} x2={W - PAD_X} y2={zeroY}
                stroke="var(--edge2)" strokeWidth="1" strokeDasharray="2 4" />
        ) : null}

        {areaPath ? <path d={areaPath} fill="url(#perf-fill)" /> : null}

        {/* Contributed capital, in the flat colour reserved for money paid in */}
        {mode === 'value' && showPaidIn ? chains.map((chain, i) => (
          <path key={`c${i}`} d={line(chain, (v) => v.y2)} fill="none"
                stroke="var(--capital)" strokeWidth="1.5" strokeLinejoin="round"
                strokeLinecap="round" />
        )) : null}

        {/* Dashed where a session went unrecorded and the figure between the two
            ends is genuinely unknown. Nights are not this: they are inside the
            chain above, carried flat and then stepped. */}
        {holes.map((h) => (
          <path key={h.key} d={h.d} fill="none" stroke="var(--accent)" strokeWidth="1.6"
                strokeOpacity="0.5" strokeDasharray="3 4" strokeLinecap="round" />
        ))}

        {benchLines.map((b, i) => {
          const style = COMPARISON[i % COMPARISON.length]!;
          return (
            <path
              key={b.label}
              d={b.points
                .map((p, j) => `${j === 0 ? 'M' : 'L'}${scale.x(p.t).toFixed(2)},${scale.y(p.v).toFixed(2)}`)
                .join(' ')}
              fill="none" stroke={style.stroke} strokeWidth="1.4" strokeLinecap="round"
              strokeDasharray={style.dash} opacity="0.85"
            />
          );
        })}

        {/* One line per unbroken stretch, nights included. */}
        {chains.map((chain, i) => (
          <path key={`v${i}`} d={line(chain, (v) => v.y1)} fill="none"
                stroke="var(--accent)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        ))}

        {/* Measured points are dots (§9.2). Rebuilt ones are not: they were
            computed from closing prices, not observed, and marking them the same
            way would let arithmetic pass for a record.
            *
            * Dots stop once there are more of them than the eye can tell apart.
            * A dot says "this one is a reading"; five hundred of them across a
            * phone's width say nothing and thicken the line into a band, which
            * is worse than no marking at all. Nothing is hidden — the line is
            * drawn through exactly those points either way. */}
        {series.length <= DOT_LIMIT ? series.filter((p) => !p.reconstructed).map((p) => (
          <circle key={p.at} cx={scale.x(p.t)} cy={scale.y(p.primary)} r="3"
                  fill="var(--bg)" stroke="var(--accent)" strokeWidth="1.8" />
        )) : null}

        {cursorPoint ? (
          <g className="chart-scrub">
            <line x1={scale.x(cursorPoint.t)} y1={0} x2={scale.x(cursorPoint.t)} y2={H}
                  stroke="var(--edge2)" strokeWidth="1" />
            <circle cx={scale.x(cursorPoint.t)} cy={scale.y(cursorPoint.primary)} r="4"
                    fill="var(--accent)" stroke="var(--bg)" strokeWidth="2" />
          </g>
        ) : null}
      </svg>

      {/* The readings themselves, as HTML over the chart: this SVG is stretched
          to the width it is given, so text inside it comes out squashed.
          *
          * Left out entirely while amounts are hidden. Every one of them would
          * print as the same four dots, which is three identical rows of noise
          * rather than a scale — and the gradations still do their job, because
          * evenly spaced lines say how far a move went relative to the others
          * without saying what it was worth. */}
      {/* The height is pinned to the plot rather than to the wrapper: this
          wrapper also holds the legend underneath, and a percentage measured
          against both would put every reading somewhere it does not belong. */}
      {!amountsHidden() ? (
        <div className="chart-scale" aria-hidden="true" style={{ height: H, bottom: 'auto' }}>
          {scale.ticks.map((v) => (
            <span key={v} style={{ top: `${(scale.y(v) / H) * 100}%` }}>
              {fmt(v)}
            </span>
          ))}
        </div>
      ) : null}

      <div className="chart-legend">
        <span><i className="legend-swatch" style={{ background: 'var(--accent)' }} />
          {mode === 'value' ? 'Value' : mode === 'gains' ? 'Gains only' : 'Return'}</span>
        {mode === 'value' && showPaidIn && series.some((p) => p.secondary !== null) ? (
          <span><i className="legend-swatch" style={{ background: 'var(--capital)' }} />Money paid in</span>
        ) : null}
        {benchLines.map((b, i) => {
          const style = COMPARISON[i % COMPARISON.length]!;
          const [on, off] = style.dash.split(' ');
          return (
            <span key={b.label}><i className="legend-swatch" style={{
              background: `repeating-linear-gradient(90deg,${style.stroke} 0 ${on}px,transparent ${on}px ${Number(on) + Number(off)}px)`,
            }} />{b.label}</span>
          );
        })}
        {holes.length > 0 ? (
          <span><i className="legend-swatch"
                   style={{ background: 'repeating-linear-gradient(90deg,var(--accent) 0 3px,transparent 3px 7px)' }} />
            gap in the record</span>
        ) : null}
        {heldShut ? (
          <span><i className="legend-swatch" style={{ background: 'var(--accent)', opacity: 0.75 }} />
            held while the market was shut</span>
        ) : null}
        {series.some((p) => p.reconstructed) ? (
          <span><i className="legend-swatch" style={{
            background: 'var(--accent)', height: 6, width: 6, borderRadius: '50%',
          }} />recorded · line rebuilt from closes</span>
        ) : null}
      </div>
    </div>
  );
}
